import { Button, Flex, Stack, Text } from '@mantine/core';
import { closeModal, openModal } from '@mantine/modals';
import { VoiceRecorder } from '@packages/chat/ui';
import { VOICE_RECORDER_LABELS } from '@shared/config';
import { useCallback, useState, type FC } from 'react';

const MODAL_ID = 'user-sintia-record-voice-message';

type RecordVoiceMessageContentProps = {
  voiceRecorderKey: number;
  onCancel: () => void;
  onSendFile: (file: File) => Promise<void>;
};

const RecordVoiceMessageContent: FC<RecordVoiceMessageContentProps> = ({
  voiceRecorderKey,
  onCancel,
  onSendFile,
}) => {
  const [recordedVoice, setRecordedVoice] = useState<{
    blob: Blob;
    mimeType: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRecording, setIsRecording] = useState(false);

  const handleSend = async () => {
    if (!recordedVoice) {
      return;
    }

    const { blob, mimeType } = recordedVoice;
    const subtype = mimeType.split('/')[1]?.split(';')[0]?.trim() ?? 'webm';

    const file = new File([blob], `voice-${Date.now()}.${subtype}`, {
      type: mimeType,
    });

    setIsSubmitting(true);
    try {
      await onSendFile(file);
    } finally {
      setIsSubmitting(false);
    }
  };

  const busy = isSubmitting;

  return (
    <Stack gap='md'>
      <VoiceRecorder
        labels={VOICE_RECORDER_LABELS}
        key={voiceRecorderKey}
        setIsRecording={setIsRecording}
        disabled={busy}
        onRecordingComplete={setRecordedVoice}
        onRecordingReset={() => setRecordedVoice(null)}
      />
      <Flex gap={8} mt={4} align='center'>
        <Button variant='outline' onClick={onCancel} disabled={busy}>
          Отмена
        </Button>
        {isRecording && (
          <Text size='sm' c='tertiary.7'>
            Закончите запись, чтобы отправить сообщение
          </Text>
        )}
        {recordedVoice != null && (
          <Button
            variant='primary'
            onClick={() => void handleSend()}
            disabled={busy}
            loading={busy}
          >
            Отправить
          </Button>
        )}
      </Flex>
    </Stack>
  );
};

export const useRecordVoiceMessageModal = () => {
  const closeRecordVoiceMessageModal = useCallback(() => {
    closeModal(MODAL_ID);
  }, []);

  const openRecordVoiceMessage = useCallback(
    ({ onSendFile }: { onSendFile: (file: File) => Promise<void> }) => {
      const voiceRecorderKey = Date.now();

      openModal({
        modalId: MODAL_ID,
        title: 'Записать голосовое сообщение',
        centered: true,
        radius: 'lg',
        padding: 'lg',
        children: (
          <RecordVoiceMessageContent
            voiceRecorderKey={voiceRecorderKey}
            onCancel={closeRecordVoiceMessageModal}
            onSendFile={onSendFile}
          />
        ),
      });
    },
    [closeRecordVoiceMessageModal]
  );

  return {
    openRecordVoiceMessage,
    closeRecordVoiceMessageModal,
  };
};
